import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { hasRenderableActions } from './renderableActions'

/**
 * The contents of an "Actions" cell: the row's controls, or a `—` when the row
 * has none.
 *
 * Use it for every actions cell, not only the ones that can go empty today. The
 * gap it closes is opened by an ordinary edit — adding a permission guard to a
 * control that never had one — and the row it appears on is usually a row the
 * author does not hold (a full-access role, an assignment whose submission was
 * deleted), so the blank cell is found by a user rather than in review.
 *
 *     actions: (
 *       <TableCell>
 *         <RowActions reason='Built-in role — it cannot be edited or deleted'>
 *           {canManage && <Button …/>}
 *           {canDelete && !row.isSystem && <Button …/>}
 *         </RowActions>
 *       </TableCell>
 *     )
 *
 * It replaces the flex wrapper these cells already had, so adopting it is a
 * swap rather than an extra element. Defaults match the common case —
 * right-aligned, `gap-1`; pass `className` for a cell that needs different
 * spacing, and `align='start'` for the tables whose actions read as a toolbar.
 *
 * `reason` explains, on hover and to a screen reader, why the row has nothing —
 * it is the sentence the empty cell was failing to say. Leave it off and the
 * placeholder still renders, announced as "no actions available". It is only
 * ever shown when the cell is empty, so it can name the row's own reason
 * without checking whether any control survived.
 */
export function RowActions({
  children,
  align = 'end',
  className,
  reason,
}: {
  /** The row's controls, guards and all. Absent — every guard false — is the case this exists for. */
  children?: ReactNode
  align?: 'start' | 'end'
  className?: string
  reason?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1',
        align === 'end' ? 'justify-end' : 'justify-start',
        className,
      )}
    >
      {hasRenderableActions(children) ? (
        children
      ) : (
        <>
          <span aria-hidden='true' className='text-muted-foreground' title={reason}>
            —
          </span>
          <span className='sr-only'>{reason ?? 'No actions available'}</span>
        </>
      )}
    </div>
  )
}
